import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import logoBlack from "../assets/images/logo_black.PNG";
import logoWhite from "../assets/images/logo_white.PNG";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollY, setScrollY] = useState(window.scrollY);
  // Ref and state to track the logo width
  const logoRef = useRef(null);
  const [logoWidth, setLogoWidth] = useState(0);
  const navigate = useNavigate();

  // Measure the logo width (and update it on mobile/resizing)
  useEffect(() => {
    if (logoRef.current) {
      setLogoWidth(logoRef.current.offsetWidth);
    }
  }, [isMobile]);

  // In mobile, recalculate logo center after layout/resize
  useEffect(() => {
    if (isMobile && logoRef.current) {
      setLogoWidth(logoRef.current.offsetWidth);
    }
  }, [isMobile, menuOpen]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (logoRef.current) {
        setLogoWidth(logoRef.current.offsetWidth);
      }
    };
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      setScrollY(window.scrollY);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const scrollToSection = (sectionName) => {
    const formattedId = sectionName.toLowerCase().replace(" ", "-");
    const section = document.getElementById(formattedId);

    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
    setMenuOpen(false); // Close menu after clicking a link
  };

  // Update scrollToSection to use navigation for main menu
  const handleNavClick = (sectionName) => {
    const lower = sectionName.toLowerCase().replace(/\s+/g, "");
    if (["product", "usecases", "contact"].includes(lower)) {
      navigate(`/${lower}`);
      setMenuOpen(false);
    } else {
      scrollToSection(sectionName);
    }
  };

  /*
    For mobile, we want the logo to move from a left offset of 20px
    to the centered position.

    • The header (mobile) has full viewport width.
    • Its center is at window.innerWidth/2.
    • To center the logo, its left offset should be: center - (logoWidth/2).
    • Our starting left is 20px.
    • Therefore the target x translation is:
          targetX = (window.innerWidth/2 - logoWidth/2) - 20.
    • We also use scroll progress to animate from 0 to that target.
  */
  let animatedLogo;
  if (isMobile) {
    const maxScrollForLogo = 100;
    const progress = Math.min(scrollY / maxScrollForLogo, 1);
    // Use window.innerWidth and logoWidth after layout for perfect centering
    const center = window.innerWidth / 2;
    const logoHalf = logoWidth / 2;
    const startLeft = 20;
    const targetX = center - logoHalf - startLeft;
    const currentX = progress * targetX;

    animatedLogo = (
      <motion.div
        ref={logoRef}
        style={{
          ...logoStyles,
          position: "absolute",
          left: startLeft,
          cursor: "pointer", // Make logo clickable
        }}
        animate={{ x: currentX }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={() => navigate('/')}
      >
        <img
          src={isScrolled ? logoWhite : logoBlack}
          alt="Dataqly"
          style={{
            height: "2.2rem", // Larger logo for mobile
            width: "auto",
            display: "block",
          }}
        />
      </motion.div>
    );
  } else {
    // In desktop view, simply render the logo without animation.
    animatedLogo = (
      <div style={logoStyles} onClick={() => navigate('/') }>
        <img
          src={logoBlack}
          alt="Dataqly"
          style={{
            height: "2.4rem",
            width: "auto",
            display: "block",
            cursor: "pointer",
          }}
        />
      </div>
    );
  }

  return (
    <header
      style={
        isMobile
          ? {
              ...mobileHeaderStyles,
              background: isScrolled ? "#000" : "#f8f8f8",
              color: isScrolled ? "#fff" : "#000",
              transition: "background 0.3s ease, color 0.3s ease",
            }
          : headerStyles
      }
    >
      {animatedLogo}
      {isMobile ? (
        <div style={mobileRightContainer}>
          <div
            style={{
              ...hamburgerMenu,
              color: isScrolled ? "#ffffff" : "#000000",
            }}
            onClick={toggleMenu}
          >
            ☰
          </div>
        </div>
      ) : (
        <div style={desktopContainer}>
          <nav style={navStyles}>
            {["Product", "Use Cases", "Contact"].map((item) => (
              <a
                key={item}
                style={linkStyles}
                onMouseOver={hoverLink}
                onMouseOut={resetLink}
                onClick={() => handleNavClick(item)}
              >
                {item}
              </a>
            ))}
          </nav>
          <div style={buttonContainerStyles}>
            <button
              style={buttonStyles}
              onMouseOver={hoverDemoButton}
              onMouseOut={resetDemoButton}
              onClick={() => handleNavClick("contact")}
            >
              Request Demo
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobile && menuOpen && (
          <>
            <div style={overlayStyles} onClick={toggleMenu}></div>
            <motion.nav
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={mobileNavStyles}
            >
              <button style={closeButton} onClick={toggleMenu}>
                ✖
              </button>
              <ul style={mobileNavListStyles}>
                {["Product", "Use Cases", "Contact"].map((item) => (
                  <li key={item}>
                    <a
                      style={mobileLinkStyles}
                      onClick={() => handleNavClick(item)}
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </header>
  );
};

/* === STYLES === */

/* Desktop Header */
const headerStyles = {
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "20px",
  width: "80%",
  maxWidth: "1200px",
  color: "#fff",
  background: "f8f8f8",
  zIndex: 1000,
};

/* Mobile Header */
const mobileHeaderStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "15px",
  color: "#fff",
  zIndex: 2000, // Increased zIndex for mobile
  background: "f8f8f8",
  transition: "background 0.3s ease",
};

/* Mobile Header (Scrolled) */
const mobileScrolledHeaderStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  width: "100vw",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "15px",
  color: "#fff",
  zIndex: 1000,
  background: "#000",
  transition: "background 0.3s ease",
};

/* Logo */
const logoStyles = {
  display: "flex",
  alignItems: "center",
};

/* Mobile Right Container */
const mobileRightContainer = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  flexGrow: 1,
  width: "100%",
};

/* Hamburger Menu */
const hamburgerMenu = {
  fontSize: "2rem",
  cursor: "pointer",
  marginRight: "25px",
};

/* Desktop Navigation */
const desktopContainer = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
};

const navStyles = {
  display: "flex",
  gap: "20px",
  justifyContent: "center",
  width: "100%",
};

/* Link Styles */
const linkStyles = {
  color: "#000000",
  textDecoration: "none",
  padding: "8px 10px",
  borderRadius: "4px",
  cursor: "pointer",
};

/* Mobile Navigation */
const mobileNavStyles = {
  position: "fixed",
  top: 0,
  right: 0,
  width: "100vw",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  background: "#000",
  zIndex: 50,
};

/* Mobile Overlay */
const overlayStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  background: "rgba(0, 0, 0, 0.5)",
  zIndex: 40,
};

const mobileNavListStyles = {
  listStyle: "none",
  textAlign: "center",
  padding: 0,
};

const mobileLinkStyles = {
  color: "#fff",
  fontSize: "1.8rem",
  textDecoration: "none",
  padding: "15px 0",
  display: "block",
};

/* Close Button */
const closeButton = {
  position: "absolute",
  top: "20px",
  right: "20px",
  fontSize: "2rem",
  color: "#fff",
  cursor: "pointer",
  background: "none",
  border: "none",
};

/* Button Container */
const buttonContainerStyles = {
  display: "flex",
  justifyContent: "flex-end",
};

/* Button */
const buttonStyles = {
  backgroundColor: "black",
  color: "#fff",
  padding: "8px 16px",
  borderRadius: "6px",
  fontSize: "0.9rem",
  cursor: "pointer",
  transition: "all 0.3s ease",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  whiteSpace: "nowrap",
};

/* Hover Effects */
const hoverLink = (e) =>
  (e.target.style.backgroundColor = "rgba(176, 176, 176, 0.3)");
const resetLink = (e) => (e.target.style.backgroundColor = "transparent");
const hoverDemoButton = (e) => (e.target.style.backgroundColor = "#00BCD4");
const resetDemoButton = (e) =>
  (e.target.style.backgroundColor = "black");

const arrowSmallStyles = { fontSize: "0.8rem" };

export default Header;
